/**
 * Where a dashboard card or activity row goes. The dashboard is the partner's way in, so every
 * number on it has to lead to the screen that explains it.
 */
export const STAT_LINKS = {
  clients: "/partner/clients",
  leads: "/partner/clients?tab=leads",
  documents: "/partner/documents",
  earnings: "/partner/payments",
  tasks: "/partner/tasks",
  messages: "/partner/alerts",
};

/** Activity carries no id of its own, so it lands on the list that holds it. */
export const activityLink = (item) => {
  if (item?.kind === "DOCUMENT_REQUEST") return STAT_LINKS.documents;
  if (item?.clientId) return `/partner/clients/${item.clientId}`;
  return null;
};

/**
 * A correction to an already approved amount is a new pending row and can be negative, so the
 * accrued bucket alone is not what the partner has earned.
 */
export const earnedTotal = (dashboard) =>
  (Number(dashboard?.commissionAccrued) || 0) +
  (Number(dashboard?.commissionApproved) || 0) +
  (Number(dashboard?.commissionPaid) || 0);

/** How many applications the dashboard lists, and from how many of the latest clients. */
export const RECENT_APPLICATION_LIMIT = 6;
export const RECENT_APPLICATION_CLIENTS = 6;

/**
 * The latest applications across the partner's clients: what the office has shared (with its
 * real status) plus requests still with the office or returned. An approved request has become
 * one of the shared applications, so it is not listed twice.
 */
export const recentApplications = (
  { clients = [], applicationsByClient = {}, requests = [] },
  limit = RECENT_APPLICATION_LIMIT
) => {
  const nameOf = (clientId) =>
    clients.find((client) => String(client.clientId) === String(clientId))?.name;

  const shared = clients.flatMap((client) =>
    (applicationsByClient[client.clientId] || []).map((app) => ({
      key: `app-${app.module}-${app.id}`,
      kind: "application",
      module: app.module,
      title: app.title,
      subtitle: app.subtitle,
      status: app.status || "In progress",
      clientId: client.clientId,
      clientName: client.name,
      at: app.updatedAt || app.createdAt || null,
    }))
  );

  const pending = requests
    .filter((row) => row?.status !== "APPROVED")
    .map((row) => ({
      key: `request-${row.id}`,
      kind: "request",
      module: row.module,
      title: row.requestDetails?.courseName || row.requestDetails?.otherName || null,
      subtitle: row.requestNote,
      status: row.status,
      clientId: row.clientId ?? null,
      clientName: row.clientName || nameOf(row.clientId) || null,
      at: row.updatedAt || row.createdAt || null,
    }));

  return [...shared, ...pending]
    .sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")))
    .slice(0, limit);
};
